import React, {
 useState, useEffect
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
 Label
} from 'shared/components/ui/label';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
 Plus, Trash2
} from 'lucide-react';
import {
 connectionService
} from 'shared/api/services/BEX';
import {
 ApiCallStepConfig, WorkflowStep
} from '../../types/workflow.types';
import PlaceholderHelper from './PlaceholderHelper';

interface ApiCallStepFormProps {
    config: ApiCallStepConfig;
    onChange: (config: ApiCallStepConfig) => void;
    steps: WorkflowStep[];
    stepIndex: number;
}

interface ConnectionOption {
    id: string;
    name: string;
    type: string;
}

const httpMethods = ['GET', 'POST', 'PUT', 'DELETE'] as const;

const methodColors: Record<string, string> = {
    GET: 'border-green-500 bg-green-50 text-green-700',
    POST: 'border-blue-500 bg-blue-50 text-blue-700',
    PUT: 'border-amber-500 bg-amber-50 text-amber-700',
    DELETE: 'border-red-500 bg-red-50 text-red-700',
};

const ApiCallStepForm: React.FC<ApiCallStepFormProps> = ({
 config, onChange, steps, stepIndex
}) => {
    const {
 appId, oragID
} = useParams<{ appId: string; oragID: string }>();
    const [connections, setConnections] = useState<ConnectionOption[]>([]);
    const [isLoadingConnections, setIsLoadingConnections] = useState(true);
    const [headerEntries, setHeaderEntries] = useState<{ key: string; value: string }[]>(() => {
        return Object.entries(config.headers || {}).map(([key, value]) => ({
 key,
value
}));
    });

    useEffect(() => {
        const loadConnections = async () => {
            if (!oragID || !appId) return;
            try {
                const data = await connectionService.getConnections(oragID, appId);
                if (Array.isArray(data)) {
                    setConnections(data.map((c: any) => ({
                        id: c.connection_id,
                        name: c.connection_name,
                        type: c.source_type,
                    })));
                }
            } catch (error) {
                console.error('Error loading connections:', error);
            } finally {
                setIsLoadingConnections(false);
            }
        };
        loadConnections();
    }, [oragID, appId]);

    const syncHeaders = (entries: { key: string; value: string }[]) => {
        const headers: Record<string, string> = {};
        entries.forEach((e) => { if (e.key.trim()) headers[e.key] = e.value; });
        onChange({
 ...config,
headers
});
    };

    const addHeader = () => {
        const newEntries = [...headerEntries, {
 key: '',
value: ''
}];
        setHeaderEntries(newEntries);
    };

    const updateHeader = (index: number, field: 'key' | 'value', val: string) => {
        const newEntries = headerEntries.map((e, i) => (i === index ? {
 ...e,
[field]: val
} : e));
        setHeaderEntries(newEntries);
        syncHeaders(newEntries);
    };

    const removeHeader = (index: number) => {
        const newEntries = headerEntries.filter((_, i) => i !== index);
        setHeaderEntries(newEntries);
        syncHeaders(newEntries);
    };

    const handlePlaceholderInsert = (placeholder: string) => {
        onChange({
 ...config,
body: (config.body || '') + placeholder
});
    };

    return (
        <div className="space-y-4">
            {/* Connection */}
            <div className="space-y-1.5">
                <Label className="text-sm">Connection</Label>
                <Select
                    value={config.connectionId}
                    onValueChange={(v) => onChange({
 ...config,
connectionId: v
})}
                    disabled={isLoadingConnections}
                >
                    <SelectTrigger>
                        <SelectValue placeholder={isLoadingConnections ? 'Loading...' : 'Select a connection'} />
                    </SelectTrigger>
                    <SelectContent>
                        {connections.map((conn) => (
                            <SelectItem key={conn.id} value={conn.id}>
                                {conn.name} ({conn.type})
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Method */}
            <div className="space-y-1.5">
                <Label className="text-sm">Method</Label>
                <div className="grid grid-cols-4 gap-2">
                    {httpMethods.map((method) => (
                        <button
                            key={method}
                            type="button"
                            className={`p-2 border rounded-md text-center text-sm font-medium transition-colors ${
                                config.method === method
                                    ? methodColors[method]
                                    : 'border-muted hover:bg-muted/50'
                            }`}
                            onClick={() => onChange({
 ...config,
method
})}
                        >
                            {method}
                        </button>
                    ))}
                </div>
            </div>

            {/* Path */}
            <div className="space-y-1.5">
                <Label className="text-sm">Path</Label>
                <Input
                    value={config.path}
                    onChange={(e) => onChange({
 ...config,
path: e.target.value
})}
                    placeholder="/api/v1/resource"
                />
            </div>

            {/* Headers */}
            <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                    <Label className="text-sm">Headers</Label>
                    <Button variant="ghost" size="sm" onClick={addHeader} className="h-7 text-xs">
                        <Plus className="mr-1 h-3 w-3" /> Add Header
                    </Button>
                </div>
                {headerEntries.length > 0 && (
                    <div className="space-y-2">
                        {headerEntries.map((entry, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <Input
                                    value={entry.key}
                                    onChange={(e) => updateHeader(idx, 'key', e.target.value)}
                                    placeholder="Header name"
                                    className="flex-1"
                                />
                                <Input
                                    value={entry.value}
                                    onChange={(e) => updateHeader(idx, 'value', e.target.value)}
                                    placeholder="Value"
                                    className="flex-1"
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 shrink-0"
                                    onClick={() => removeHeader(idx)}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Body (for POST/PUT) */}
            {(config.method === 'POST' || config.method === 'PUT') && (
                <div className="space-y-1.5">
                    <Label className="text-sm">Body</Label>
                    <Textarea
                        value={config.body}
                        onChange={(e) => onChange({
 ...config,
body: e.target.value
})}
                        placeholder='{"key": "value"}'
                        rows={5}
                        className="font-mono text-sm resize-none"
                    />
                </div>
            )}

            {/* Placeholder Helper */}
            <PlaceholderHelper
                steps={steps}
                currentStepIndex={stepIndex}
                onInsert={handlePlaceholderInsert}
            />
        </div>
    );
};

export default ApiCallStepForm;
